import { useEffect } from 'react';
import { throttle } from 'lodash';

import { LOGOUT_TIME_KEY } from '../constants';

const calcEndDate = (startTime: number) => Date.now() + startTime * 1000;
const setLogoutTime = (startTime: number) => () => localStorage.setItem(LOGOUT_TIME_KEY, `${calcEndDate(startTime)}`);
const getLogoutTime = (startTime: number, skipCalc?: boolean): number => {
  const storedVal = localStorage.getItem(LOGOUT_TIME_KEY);

  let returnVal;
  if (typeof storedVal === 'string') {
    returnVal = parseInt(storedVal, 10);
  }

  return returnVal || (skipCalc ? 0 : calcEndDate(startTime));
};

const useAutoLogout = (startTime: number, loggedIn: boolean, logoutFn: () => Promise<void>) => {
  useEffect(() => {
    if (!loggedIn) {
      // Just in case this still somehow exists
      localStorage.removeItem(LOGOUT_TIME_KEY);
      return;
    }

    const existingTime = getLogoutTime(startTime, true);

    // When a user revisits the site they should be logged out if it was longer than 15 minutes since their last visit
    if (existingTime && Date.now() >= existingTime) {
      localStorage.removeItem(LOGOUT_TIME_KEY);
      logoutFn();
      return;
    }

    const setNewLogoutTime = throttle(setLogoutTime(startTime), 500, { trailing: false });
    setNewLogoutTime();

    const userInterval = setInterval(() => {
      if (Date.now() >= getLogoutTime(startTime)) {
        logoutFn();
        cleanUp();
      }
    }, 1000);

    const events = ['pointermove', 'pointerdown', 'pointerup', 'click', 'scroll', 'keypress'];

    for (const event of events) {
      window.addEventListener(event, setNewLogoutTime);
    }

    const cleanUp = () => {
      clearInterval(userInterval);
      localStorage.removeItem(LOGOUT_TIME_KEY);

      for (const event of events) {
        window.removeEventListener(event, setNewLogoutTime);
      }
    };

    return cleanUp;
  }, [loggedIn]);
};

export default useAutoLogout;
